import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, Plus, Euro, Users2, Zap, Loader2, X, Trash2, Bot, Activity, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  lead: { label: "Lead", color: "bg-slate-500/15 text-slate-300" },
  contacted: { label: "Contactado", color: "bg-brand-blue/15 text-brand-blue" },
  onboarding: { label: "Onboarding", color: "bg-brand-orange/15 text-brand-orange" },
  active: { label: "Ativo", color: "bg-brand-success/15 text-brand-success" },
  churned: { label: "Perdido", color: "bg-destructive/15 text-destructive" },
};

const empty = { name: "", contact_name: "", contact_email: "", phone: "", plan: "Growth", status: "lead", monthly_value: 0, notes: "" };

export default function CRM() {
  const [agencies, setAgencies] = useState(null);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [activities, setActivities] = useState([]);

  const load = async () => {
    const [a, s] = await Promise.all([api.get("/agencies"), api.get("/crm/stats")]);
    setAgencies(a.data); setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...empty, ...a }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, monthly_value: Number(form.monthly_value) };
      if (editing) await api.put(`/agencies/${editing.id}`, payload);
      else await api.post("/agencies", payload);
      toast.success(editing ? "Agência atualizada" : "Agência criada");
      setOpen(false);
      await load();
      if (detail && editing?.id === detail.id) openDetail({ ...detail, ...payload });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/agencies/${id}`);
    toast.success("Agência removida");
    if (detail?.id === id) setDetail(null);
    await load();
  };

  const openDetail = async (a) => {
    setDetail(a);
    const { data } = await api.get(`/agencies/${a.id}/activities`);
    setActivities(data);
  };

  if (!agencies || !stats) return <div className="h-96 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-blue" /></div>;

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2">
            <Building2 className="text-brand-blue" /> CRM
          </h1>
          <p className="text-brand-muted text-sm mt-1">Gestão de agências clientes com automatismos por fase do funil.</p>
        </div>
        <Button onClick={openNew} data-testid="new-agency-button" className="bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold">
          <Plus size={16} className="mr-1" /> Nova agência
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-5 rounded-sm bg-brand-surface border border-brand-border">
          <div className="h-10 w-10 rounded-sm bg-brand-success/15 text-brand-success flex items-center justify-center"><Euro size={18} /></div>
          <p className="font-heading font-black text-2xl mt-3">€{stats.mrr}</p>
          <p className="text-sm text-brand-muted">MRR (receita mensal)</p>
        </div>
        <div className="p-5 rounded-sm bg-brand-surface border border-brand-border">
          <div className="h-10 w-10 rounded-sm bg-brand-blue/15 text-brand-blue flex items-center justify-center"><Users2 size={18} /></div>
          <p className="font-heading font-black text-2xl mt-3">{stats.total}</p>
          <p className="text-sm text-brand-muted">Agências no pipeline</p>
        </div>
        <div className="p-5 rounded-sm bg-brand-surface border border-brand-border">
          <div className="h-10 w-10 rounded-sm bg-brand-success/15 text-brand-success flex items-center justify-center"><Zap size={18} /></div>
          <p className="font-heading font-black text-2xl mt-3">{stats.pipeline.active}</p>
          <p className="text-sm text-brand-muted">Clientes ativos</p>
        </div>
        <div className="p-5 rounded-sm bg-brand-surface border border-brand-border">
          <div className="h-10 w-10 rounded-sm bg-brand-orange/15 text-brand-orange flex items-center justify-center"><Activity size={18} /></div>
          <p className="font-heading font-black text-2xl mt-3">{stats.pipeline.lead + stats.pipeline.contacted + stats.pipeline.onboarding}</p>
          <p className="text-sm text-brand-muted">Em prospeção</p>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-sm bg-brand-surface border border-brand-border overflow-hidden">
        <div className="hidden md:grid grid-cols-[2fr_1.5fr_1fr_1fr_auto] gap-4 px-5 py-3 border-b border-brand-border text-xs uppercase tracking-wider text-brand-muted font-semibold">
          <span>Agência</span><span>Contacto</span><span>Estado</span><span>Valor/mês</span><span></span>
        </div>
        {agencies.map((a) => (
          <div key={a.id} data-testid="agency-row"
            className="grid grid-cols-2 md:grid-cols-[2fr_1.5fr_1fr_1fr_auto] gap-4 px-5 py-4 border-b border-brand-border last:border-0 items-center hover:bg-white/[0.02] cursor-pointer"
            onClick={() => openDetail(a)}>
            <div className="min-w-0">
              <p className="font-medium truncate">{a.name}</p>
              <p className="text-xs text-brand-muted">{a.plan}</p>
            </div>
            <div className="hidden md:block min-w-0">
              <p className="text-sm truncate">{a.contact_name || "—"}</p>
              <p className="text-xs text-brand-muted truncate">{a.contact_email}</p>
            </div>
            <div>
              <span className={`text-xs px-2 py-1 rounded-sm font-medium ${STATUS[a.status]?.color}`}>{STATUS[a.status]?.label}</span>
            </div>
            <div className="hidden md:block text-sm font-semibold">€{a.monthly_value || 0}</div>
            <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => openEdit(a)} className="text-brand-muted hover:text-brand-blue" data-testid="edit-agency"><Pencil size={15} /></button>
              <button onClick={() => remove(a.id)} className="text-brand-muted hover:text-destructive" data-testid="delete-agency"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {agencies.length === 0 && <p className="p-8 text-center text-brand-muted">Sem agências. Adicione a primeira!</p>}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-surface border-brand-border max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Editar agência" : "Nova agência"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="agency-name" className="mt-1 bg-background border-brand-border rounded-sm" /></div>
              <div><Label>Plano</Label><Input value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="mt-1 bg-background border-brand-border rounded-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contacto</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1 bg-background border-brand-border rounded-sm" /></div>
              <div><Label>Email</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1 bg-background border-brand-border rounded-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-background border-brand-border rounded-sm" /></div>
              <div>
                <Label>Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 bg-background border-brand-border rounded-sm" data-testid="agency-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Valor/mês (€)</Label><Input type="number" value={form.monthly_value} onChange={(e) => setForm({ ...form, monthly_value: e.target.value })} data-testid="agency-value" className="mt-1 bg-background border-brand-border rounded-sm" /></div>
            </div>
            <div><Label>Notas</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-background border-brand-border rounded-sm" /></div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="save-agency-button" className="bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="agency-detail">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDetail(null)} />
          <div className="relative w-full max-w-md bg-brand-surface border-l border-brand-border overflow-y-auto">
            <div className="px-6 py-4 border-b border-brand-border flex items-center justify-between sticky top-0 bg-brand-surface z-10">
              <h2 className="font-heading font-bold text-lg truncate">{detail.name}</h2>
              <button onClick={() => setDetail(null)} className="text-brand-muted hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-sm font-medium ${STATUS[detail.status]?.color}`}>{STATUS[detail.status]?.label}</span>
                <span className="text-sm text-brand-muted">{detail.plan}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-brand-muted">Contacto</p><p>{detail.contact_name || "—"}</p></div>
                <div><p className="text-xs text-brand-muted">Email</p><p className="truncate">{detail.contact_email || "—"}</p></div>
                <div><p className="text-xs text-brand-muted">Telefone</p><p>{detail.phone || "—"}</p></div>
                <div><p className="text-xs text-brand-muted">Valor/mês</p><p className="font-semibold">€{detail.monthly_value || 0}</p></div>
              </div>
              {detail.notes && <div><p className="text-xs text-brand-muted mb-1">Notas</p><p className="text-sm text-white/85">{detail.notes}</p></div>}
              <Button onClick={() => openEdit(detail)} variant="outline" className="w-full border-brand-border rounded-sm">
                <Pencil size={15} className="mr-1" /> Editar
              </Button>

              <div className="pt-4 border-t border-brand-border">
                <h3 className="font-heading font-semibold flex items-center gap-2 mb-3"><Activity size={16} className="text-brand-orange" /> Histórico & automatismos</h3>
                <div className="space-y-2">
                  {activities.map((act) => (
                    <div key={act.id} className={`flex gap-3 p-3 rounded-sm border ${act.type === "automation" ? "border-brand-orange/30 bg-brand-orange/5" : "border-brand-border"}`}>
                      {act.type === "automation" ? <Bot size={15} className="text-brand-orange mt-0.5 shrink-0" /> : <Activity size={15} className="text-brand-muted mt-0.5 shrink-0" />}
                      <div>
                        <p className="text-sm">{act.description}</p>
                        <p className="text-xs text-brand-muted mt-0.5">{new Date(act.created_at).toLocaleString("pt-PT")}</p>
                      </div>
                    </div>
                  ))}
                  {activities.length === 0 && <p className="text-sm text-brand-muted">Sem atividades registadas.</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
